/**
 * Analytics empty state.
 *
 * Used by both AnalyticsScreen and AnalyticsDrilldown when the period
 * yields zero matching transactions. Renders a coffee-cup illustration
 * (cream/mocha palette) with caption + optional hint, on a surface card
 * so the screen layout stays calm.
 */
import type { CSSProperties } from 'react';

export interface EmptyAnalyticsProps {
  /** Caption beneath the illustration (e.g. "本月還沒有支出記錄"). */
  caption: string;
  /** Optional secondary line. */
  hint?: string;
  style?: CSSProperties;
}

export default function EmptyAnalytics({
  caption,
  hint,
  style,
}: EmptyAnalyticsProps): JSX.Element {
  return (
    <div
      style={{
        padding: '36px 24px',
        textAlign: 'center',
        background: 'var(--bg-surface)',
        borderRadius: 16,
        boxShadow: 'var(--shadow-1)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        ...style,
      }}
    >
      <svg
        width="84"
        height="84"
        viewBox="0 0 84 84"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="42" cy="42" r="40" fill="var(--cream-100)" />
        <path
          d="M22 38h32v14a10 10 0 0 1-10 10H32a10 10 0 0 1-10-10V38z"
          fill="var(--sand-300)"
        />
        <path
          d="M54 42h6a6 6 0 0 1 0 12h-6"
          fill="none"
          stroke="var(--sand-400)"
          strokeWidth="2.4"
          strokeLinecap="round"
        />
        <path
          d="M30 22c0 4 4 4 4 8M40 22c0 4 4 4 4 8M50 22c0 4 4 4 4 8"
          fill="none"
          stroke="var(--mocha-600)"
          strokeWidth="2.4"
          strokeLinecap="round"
          opacity="0.7"
        />
      </svg>
      <div
        style={{
          font: '500 14px var(--font-cjk)',
          color: 'var(--fg-2)',
        }}
      >
        {caption}
      </div>
      {hint && (
        <div
          style={{
            font: '400 12px var(--font-cjk)',
            color: 'var(--fg-3)',
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}
